import { addMethod, string, object, boolean } from 'yup';
import Api from 'api';
import { storage } from 'store';
import { getCachedDnsCheck, setCachedDnsCheck } from './services';

export const basicEmailValidation = value => {
  // RFC 3696-compliant email regex (practical version)
  // - local part: up to 64 chars, domain: up to 255 chars, total: up to 254 chars
  // - no consecutive dots, no leading/trailing dot in local part
  const emailRegex =
    /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_{|}~`\-‘]+(\.[a-zA-Z0-9!#$%&'*+/=?^_{|}~`\-‘]+)*@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

  if (!emailRegex.test(value)) {
    storage.addItem('isEmailCheckedSuccessful', false);
    return false;
  }

  return true;
};

const emailDnsValidation = async email => {
  const cachedResult = getCachedDnsCheck(email);

  if (cachedResult !== undefined) {
    storage.addItem('isEmailCheckedSuccessful', cachedResult);
    return cachedResult;
  }

  try {
    storage.addItem('isEmailCheckLoading', true);
    storage.addItem('isEmailCheckedSuccessful', false);

    const dnsCheck = await Api.getDnsCheck(email);

    const checkStatus = dnsCheck?.status || false;
    setCachedDnsCheck(email, checkStatus);
    storage.addItem('isEmailCheckedSuccessful', checkStatus);

    return checkStatus;
  } catch (error) {
    storage.addItem('isEmailCheckedSuccessful', false);
    setCachedDnsCheck(email, false);

    return false;
  } finally {
    storage.addItem('isEmailCheckLoading', false);
  }
};

export const emailValidation = async value => {
  const isBasicValid = basicEmailValidation(value);

  if (isBasicValid) {
    return await emailDnsValidation(value);
  }

  return false;
};

const phoneValidation = value => {
  if (!value) {
    return false;
  }

  return value.length >= 5 && value.length <= 18;
};

const emojiValidation = value => {
  const regEx =
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F1FF}\u{1F200}-\u{1F2FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{25A0}-\u{25FF}\u{2B00}-\u{2BFF}\u{3030}\u{303D}\u{3297}\u{3299}]/u;

  return !regEx.test(value);
};

const specialSymbolsValidation = value => {
  const regEx = /[<>{}^]/u;

  return !regEx.test(value);
};

addMethod(string, 'checkSpecialSymbols', function (message) {
  return this.test('checkSpecialSymbols', message, function (value) {
    return specialSymbolsValidation(value);
  });
});

addMethod(string, 'checkEmoji', function (message) {
  return this.test('checkEmoji', message, function (value) {
    return emojiValidation(value);
  });
});

addMethod(string, 'checkEmail', function (message) {
  return this.test('checkEmail', message, async function (value) {
    return emailValidation(value);
  });
});

addMethod(string, 'checkPhone', function (message) {
  return this.test('checkPhone', message, function (value) {
    return phoneValidation(value);
  });
});

const corporateEmail = messages => {
  return string()
    .required(messages.email.required)
    .checkEmoji(messages.emoji)
    .checkEmail(messages.email.check);
};

const phone = messages => {
  return string()
    .required(messages.phone)
    .checkPhone(messages.phone)
    .checkEmoji(messages.emoji)
    .checkSpecialSymbols(messages.specialSymbols);
};

export const corporateFormSchema = messages => {
  return object().shape(
    {
      name: string()
        .trim()
        .required(messages.name)
        .checkEmoji(messages.emoji)
        .checkSpecialSymbols(messages.specialSymbols),
      email: corporateEmail(messages),
      phone: phone(messages),
      company: string()
        .trim()
        .required(messages.company)
        .checkEmoji(messages.emoji)
        .checkSpecialSymbols(messages.specialSymbols),
      message: string()
        .trim()
        .required(messages.message)
        .checkEmoji(messages.emoji)
        .checkSpecialSymbols(messages.specialSymbols),
      NDA: boolean(),
    },
    ['email', 'phone'],
  );
};
